import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, ExternalLink, Search } from "lucide-react";
import dayjs from "dayjs";
import { startCase } from "lodash";
import {
  panelTitleSx,
  touchTargetSx,
  workspaceSwitchSx,
} from "../../overviewPanel";
import type { InsiderTradingData } from "../hooks/useInsiderTrading";
import type { PriceRecordEod } from "../hooks/useCompanyPriceEod";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import {
  formatShareCount,
  getFilingUrl,
  getInsiderTradeKind,
  getInsiderTradeLabel,
  getInsiderTradePrices,
  InsiderTradePrice,
  InsiderSortField,
  InsiderSortDirection,
  sortInsiderTransactions,
} from "./insiderActivityData";

const PAGE_SIZE = 15;
const filters = [
  { value: "all", label: "All activity" },
  { value: "purchase", label: "Purchases" },
  { value: "sale", label: "Sales" },
  { value: "other", label: "Other" },
];
const formatRole = (role: string) =>
  startCase((role.split(":").pop() ?? role).trim().toLowerCase()).replace(
    /\b(ceo|cfo|coo|cto|cao|evp|svp)\b/gi,
    (acronym) => acronym.toUpperCase(),
  );

interface TradeLabelProps {
  type: string;
  cashEquity?: boolean;
}

const TradeLabel: React.FC<TradeLabelProps> = ({ type, cashEquity }) => {
  const theme = useTheme();
  const kind = getInsiderTradeKind(type, { cashEquity });
  let color = theme.palette.text.secondary;
  if (kind === "purchase") color = theme.palette.success.main;
  if (kind === "purchase" && theme.palette.mode === "light")
    color = darken(theme.palette.success.dark, 0.2);
  if (kind === "sale") color = theme.palette.error.main;
  if (kind === "sale" && theme.palette.mode === "light")
    color = theme.palette.error.dark;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color,
        fontWeight: 500,
        fontSize: "0.8rem",
        whiteSpace: "nowrap",
      }}
    >
      {kind === "other" && (
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            bgcolor: color,
            mr: 0.5,
          }}
        />
      )}
      {getInsiderTradeLabel(type)}
    </Box>
  );
};

interface FilingLinkProps {
  trade: InsiderTradingData;
}

const FilingLink: React.FC<FilingLinkProps> = ({ trade }) => {
  const url = getFilingUrl(trade.url);
  if (!url)
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  const label = `View ${trade.reportingName} filing (${trade.formType}) from ${dayjs(trade.filingDate).format("MMM D, YYYY")}`;
  return (
    <Tooltip title={label}>
      <IconButton
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        size="small"
        sx={{ ...touchTargetSx, color: "text.secondary" }}
      >
        <ExternalLink size={15} />
      </IconButton>
    </Tooltip>
  );
};

interface TradeAmountProps {
  valuation?: InsiderTradePrice;
  shares?: number;
}

const TradeAmount: React.FC<TradeAmountProps> = ({ valuation, shares }) => {
  const { formatPrice } = useStockCurrency();
  const value = (valuation?.price ?? NaN) * (shares ?? 1);
  if (!Number.isFinite(value) || value <= 0) return <>—</>;
  // A trade's total is thousands to millions; cents on it only hide the scale.
  const formatted =
    shares === undefined ? formatPrice(value) : formatPrice(value, 0);
  if (!valuation?.estimatedFrom) return <>{formatted}</>;
  const date = dayjs(valuation.estimatedFrom).format("MMM D, YYYY");
  const label =
    shares === undefined
      ? "Estimated price per share"
      : "Estimated market value";
  const basis = `${formatPrice(valuation.price)} closing price on ${date}`;
  const explanation =
    shares === undefined
      ? basis
      : `${formatShareCount(shares)} shares × ${basis}`;
  return (
    <Tooltip
      title={`${label}: ${explanation}. Market estimate, not a reported transaction price.`}
    >
      <Box
        component="span"
        tabIndex={0}
        aria-label={`${label}: ${formatted}, based on the ${date} close`}
        sx={{
          cursor: "help",
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        ≈ {formatted}
      </Box>
    </Tooltip>
  );
};

const SORT_LABELS: Record<InsiderSortField, string> = {
  tradeDate: "Trade date",
  shares: "Shares",
  totalValue: "Total value",
  ownedAfter: "Owned after",
};

type TransactionSort = {
  field: InsiderSortField;
  direction: InsiderSortDirection;
};

interface SortableColumnProps {
  field: InsiderSortField;
  sort: TransactionSort;
  onSort: (field: InsiderSortField) => void;
}

const SortableColumn: React.FC<SortableColumnProps> = ({
  field,
  sort,
  onSort,
}) => {
  const active = sort.field === field;
  const direction = active ? sort.direction : "desc";
  const alignment = field === "tradeDate" ? "left" : "right";
  const className = field === "totalValue" ? "transaction-total" : undefined;
  const sortDirection = active ? direction : false;
  return (
    <TableCell
      align={alignment}
      className={className}
      sortDirection={sortDirection}
    >
      <TableSortLabel
        active={active}
        direction={direction}
        onClick={() => onSort(field)}
        aria-label={`Sort by ${SORT_LABELS[field].toLowerCase()}`}
      >
        {SORT_LABELS[field]}
      </TableSortLabel>
    </TableCell>
  );
};

const EMPTY_PRICES: PriceRecordEod[] = [];

interface InsiderTransactionsTableProps {
  trades: InsiderTradingData[];
  prices?: PriceRecordEod[];
  /** False when the viewed listing is an ADS: its close cannot value an award of underlying shares. */
  estimateAwards?: boolean;
  selection?: { trade: InsiderTradingData } | null;
}

export const InsiderTransactionsTable: React.FC<
  InsiderTransactionsTableProps
> = ({ trades, prices = EMPTY_PRICES, estimateAwards = true, selection }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const valuations = useMemo(
    () => getInsiderTradePrices(trades, prices, { estimateAwards }),
    [trades, prices, estimateAwards],
  );
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<TransactionSort>({
    field: "tradeDate",
    direction: "desc",
  });
  const orderedTrades = useMemo(
    () =>
      sortInsiderTransactions(trades, valuations, sort.field, sort.direction),
    [trades, valuations, sort.field, sort.direction],
  );
  const onSort = (field: InsiderSortField) => {
    setSort((current) => {
      const direction =
        current.field === field && current.direction === "desc"
          ? "asc"
          : "desc";
      return { field, direction };
    });
    setPage(0);
  };
  const sizeOrder =
    sort.direction === "desc" ? "highest first" : "lowest first";
  let sortDescription = `${SORT_LABELS[sort.field]} · ${sizeOrder}`;
  if (sort.field === "tradeDate")
    sortDescription =
      sort.direction === "desc" ? "Newest first" : "Oldest first";
  const selectedRow = useRef<HTMLElement | null>(null);
  const pendingSelection = useRef(false);
  const revealedSelection = useRef<typeof selection>(undefined);
  useEffect(() => {
    if (!selection || revealedSelection.current === selection) return;
    const index = orderedTrades.indexOf(selection.trade);
    if (index < 0) return;
    revealedSelection.current = selection;
    pendingSelection.current = true;
    setFilter("all");
    setSearch("");
    setPage(Math.floor(index / PAGE_SIZE));
  }, [selection, orderedTrades]);
  const filtered = useMemo(
    () =>
      orderedTrades.filter((trade) => {
        const matchesType =
          filter === "all" ||
          getInsiderTradeKind(trade.transactionType, trade) === filter;
        const matchesSearch = `${trade.reportingName} ${trade.typeOfOwner}`
          .toLowerCase()
          .includes(search.trim().toLowerCase());
        return matchesType && matchesSearch;
      }),
    [orderedTrades, filter, search],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages - 1);
  const visible = filtered.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE,
  );
  useEffect(() => {
    if (!selection || !pendingSelection.current || !selectedRow.current) return;
    selectedRow.current.scrollIntoView({
      block: "center",
      behavior: "instant",
    });
    selectedRow.current.focus({ preventScroll: true });
    pendingSelection.current = false;
  }, [selection, currentPage, filter, search, isMobile]);
  const selectedStyles = {
    bgcolor: alpha(theme.palette.primary.main, 0.1),
    outline: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    outlineOffset: -1,
  };
  const hairline = alpha(theme.palette.text.primary, 0.07);
  const hasFilters = search.length > 0 || filter !== "all";

  return (
    <Box component="section" aria-label="Insider transactions">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography
          component="h3"
          sx={{
            ...panelTitleSx,
            color: "text.primary",
            borderLeft: 2,
            borderColor: "primary.main",
            pl: 1,
            lineHeight: 1.5,
          }}
        >
          Transactions
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: "0.6875rem",
            fontVariantNumeric: "tabular-nums",
            color: "text.secondary",
            bgcolor: "action.hover",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
          }}
        >
          {formatShareCount(trades.length)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: "auto" }}
        >
          {sortDescription}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value: string | null) => {
            if (value) {
              setFilter(value);
              setPage(0);
            }
          }}
          aria-label="Filter transaction type"
          size="small"
          sx={{ ...workspaceSwitchSx, flexWrap: "wrap" }}
        >
          {filters.map((item) => (
            <ToggleButton key={item.value} value={item.value}>
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          placeholder="Search insider or role"
          size="small"
          slotProps={{
            htmlInput: { "aria-label": "Search insider or role" },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            width: { xs: "100%", sm: 260 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              bgcolor: "transparent",
              fontSize: "0.8rem",
            },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: hairline },
          }}
        />
      </Box>
      {!isMobile && (
        <TableContainer sx={{ bgcolor: "transparent" }}>
          <Table
            size="small"
            aria-label="Insider trading transactions"
            sx={{
              "& .MuiTableCell-root": {
                borderBottom: `1px solid ${hairline}`,
                px: 1.5,
                py: 1.5,
                fontSize: "0.75rem",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              },
              "& .MuiTableCell-head": {
                bgcolor: "transparent",
                color: "text.secondary",
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                py: 1.25,
              },

              "& .MuiTableSortLabel-root": {
                color: "inherit",
                "&:hover, &.Mui-active": { color: "primary.main" },
                "& .MuiTableSortLabel-icon": {
                  fontSize: 14,
                  opacity: 0.35,
                  color: "inherit",
                },
                "&.Mui-active .MuiTableSortLabel-icon": {
                  opacity: 1,
                  color: "primary.main",
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 3,
                },
              },
              "& .transaction-figure": {
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: "text.primary",
                letterSpacing: "-0.02em",
              },
              "& .transaction-total": {
                bgcolor: alpha(theme.palette.text.primary, 0.025),
                borderLeft: `1px solid ${hairline}`,
              },
              "& .MuiTableBody-root .transaction-total": {
                fontSize: "1rem",
                fontWeight: 700,
              },
              "& .MuiTableRow-root:hover": {
                bgcolor: alpha(theme.palette.text.primary, 0.025),
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Insider / role</TableCell>
                <TableCell>Transaction</TableCell>
                <SortableColumn field="tradeDate" sort={sort} onSort={onSort} />
                <TableCell align="right">Filing</TableCell>
                <SortableColumn
                  field="ownedAfter"
                  sort={sort}
                  onSort={onSort}
                />
                <TableCell align="right">Price / share</TableCell>
                <SortableColumn field="shares" sort={sort} onSort={onSort} />
                <SortableColumn
                  field="totalValue"
                  sort={sort}
                  onSort={onSort}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((trade, index) => (
                <TableRow
                  key={`${trade.reportingCik}-${trade.transactionDate}-${index}`}
                  ref={(node) => {
                    if (trade === selection?.trade) selectedRow.current = node;
                  }}
                  tabIndex={-1}
                  aria-selected={trade === selection?.trade}
                  sx={{ ...(trade === selection?.trade && selectedStyles) }}
                >
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                        whiteSpace: "normal",
                      }}
                    >
                      {trade.reportingName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.6875rem",
                        color: "text.secondary",
                        mt: 0.3,
                        whiteSpace: "normal",
                      }}
                    >
                      {formatRole(trade.typeOfOwner)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TradeLabel type={trade.transactionType} cashEquity={trade.cashEquity} />
                    {/* The security named on the filing — the only reliable
                        statement of which share class traded. The row's
                        `symbol` is a filer-typed label and is never shown. */}
                    {trade.securityName && (
                      <Typography
                        sx={{
                          fontSize: "0.6875rem",
                          color: "text.secondary",
                          mt: 0.3,
                          maxWidth: 220,
                          whiteSpace: "normal",
                        }}
                      >
                        {trade.securityName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {dayjs(trade.transactionDate).format("MMM D, YYYY")}
                  </TableCell>
                  <TableCell align="right">
                    <FilingLink trade={trade} />
                  </TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {formatShareCount(trade.securitiesOwned)}
                  </TableCell>
                  <TableCell align="right" className="transaction-figure">
                    <TradeAmount valuation={valuations.get(trade)} />
                  </TableCell>
                  <TableCell align="right" className="transaction-figure">
                    {formatShareCount(Math.abs(trade.securitiesTransacted))}
                  </TableCell>
                  <TableCell
                    align="right"
                    className="transaction-figure transaction-total"
                  >
                    <TradeAmount
                      valuation={valuations.get(trade)}
                      shares={Math.abs(trade.securitiesTransacted)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {isMobile && (
        <Stack>
          {visible.map((trade, index) => (
            <Box
              key={`${trade.reportingCik}-${trade.transactionDate}-${index}`}
              ref={(node: HTMLElement | null) => {
                if (trade === selection?.trade) selectedRow.current = node;
              }}
              tabIndex={-1}
              data-selected={trade === selection?.trade}
              sx={{
                px: 1.5,
                py: 2,
                borderBottom: `1px solid ${hairline}`,
                ...(trade === selection?.trade && selectedStyles),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                    {trade.reportingName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6875rem" }}
                  >
                    {formatRole(trade.typeOfOwner)}
                  </Typography>
                </Box>
                <TradeLabel type={trade.transactionType} cashEquity={trade.cashEquity} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {dayjs(trade.transactionDate).format("MMM D, YYYY")}
                  {trade.securityName && ` · ${trade.securityName}`}
                </Typography>
                <FilingLink trade={trade} />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr) minmax(0, 1.5fr)",
                    sm: "1fr 1fr 1fr 1.3fr",
                  },
                  columnGap: 2,
                  rowGap: 1.5,
                  mt: 1.5,
                  fontVariantNumeric: "tabular-nums",
                  "& .amount-label": {
                    fontSize: "0.625rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "text.secondary",
                    mb: 0.5,
                  },
                  "& .amount-value": {
                    fontSize: { xs: "1rem", sm: "1.0625rem" },
                    fontWeight: 600,
                    lineHeight: 1.2,
                    letterSpacing: "-0.025em",
                    overflowWrap: "anywhere",
                  },
                }}
              >
                <Box>
                  <Typography className="amount-label">Owned after</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatShareCount(trade.securitiesOwned)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "right", sm: "left" } }}>
                  <Typography className="amount-label">
                    Price / share
                  </Typography>
                  <Typography className="amount-value">
                    <TradeAmount valuation={valuations.get(trade)} />
                  </Typography>
                </Box>
                <Box>
                  <Typography className="amount-label">Shares</Typography>
                  <Typography className="amount-value">
                    {formatShareCount(Math.abs(trade.securitiesTransacted))}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography className="amount-label">Total value</Typography>
                  <Typography
                    className="amount-value"
                    sx={{
                      "&&": {
                        fontSize: { xs: "1.125rem", sm: "1.25rem" },
                        fontWeight: 700,
                      },
                    }}
                  >
                    <TradeAmount
                      valuation={valuations.get(trade)}
                      shares={Math.abs(trade.securitiesTransacted)}
                    />
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
      {filtered.length === 0 && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="body2" fontWeight={500}>
            No matching transactions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Try another period, transaction type, or insider name.
          </Typography>
          {hasFilters && (
            <Button
              size="small"
              onClick={() => {
                setSearch("");
                setFilter("all");
                setPage(0);
              }}
              sx={{ mt: 1 }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      )}
      {filtered.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {currentPage * PAGE_SIZE + 1}–
            {Math.min((currentPage + 1) * PAGE_SIZE, filtered.length)} of{" "}
            {formatShareCount(filtered.length)} transactions
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              aria-label="Previous transaction page"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
              sx={touchTargetSx}
            >
              <ChevronLeft size={18} />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              {currentPage + 1} / {pages}
            </Typography>
            <IconButton
              size="small"
              aria-label="Next transaction page"
              disabled={currentPage === pages - 1}
              onClick={() => setPage(currentPage + 1)}
              sx={touchTargetSx}
            >
              <ChevronRight size={18} />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};
